/* global themeVars, wombatWidgetsConfig */

import { getParameters } from '../../js/front/analytics';
import { getGoSession, loadGoFramework, onGoReady } from '../../js/front/go-session';
import './check-my-numbers-styles';
import {
	addSignOutDesktopItem,
	addSignOutMobileItem,
	enableRegisterDateOfBirthPicker,
	changeRegisterWidgetsTexts,
	changeLoginWidgetsTexts,
	changeForgotPasswordWidgetsTexts
} from './widgets-elements';

/**
 * Tracks the go_fw authentication state and reflects it on <body> as
 * `go-authenticated` / `go-unauthenticated` classes, plus dispatches a
 * `go:sessionStatusUpdate` custom event so other scripts on the page can
 * react without depending on `go_fw` directly.
 */
let goSessionInitialized = false;

// Tracks which promotion-list widget type is active on the current page
// ('giveaways' or 'second-chance'), so PROMOTION_DETAILS navigation can
// redirect to the correct detail page without relying on navigate.data.type.
let activePromotionListType = null;

const routes = wombatWidgetsConfig?.routes || {};

// Page that account-activation e-mail links point to, and the query
// parameter carrying the activation key on that page, e.g.:
// https://example.com//account/activate/?key=eyJhbGciOi...
const ACTIVATION_PATH = routes?.activationPath ?? '';
const ACTIVATION_PARAM = routes?.activationParam ?? '';
const PROMOTION_DETAIL_PARAM = routes?.promotionDetailParam ?? '';
const TERMS_OF_USE_PATH = routes?.termsOfUsePath ?? '';
const PRIVACY_POLICY_PATH = routes?.privacyPolicyPath ?? '';
const REGISTER_PATH = routes?.registerPath ?? '';
const FORGOT_PASSWORD_PATH = routes?.forgotPasswordPath ?? '';
const SIGN_IN_PATH = routes?.signInPath ?? '';
const ACCOUNT_PATH = routes?.accountPath ?? '';
const NUMBER_CHECKER_PATH = routes?.numberCheckerPath ?? '';
const TICKET_ENTRY_PATH = routes?.ticketEntryPath ?? '';
const HISTORY_PATH = routes?.historyPath ?? '';
const GIVEAWAY_DETAIL_PATH = routes?.giveawayDetailPath ?? '';
const SECOND_CHANCE_DETAIL_PATH = routes?.secondChanceDetailPath ?? '';
const HOME_URL = themeVars.homeUrl || '/';
const mobileBreakPoint = window.matchMedia( '(max-width: 786px)' );

const activationPathname = new URL( ACTIVATION_PATH, window.location.origin ).pathname.replace( /\/$/, '' );

/**
 * Reads the activation key from the current URL, if we're on the
 * activation page and the key is present.
 *
 * @return {string|null} The activation key, or null if not applicable.
 */
const getActivationKey = () => {

	if ( window.location.pathname.replace( /\/$/, '' ) !== activationPathname ) {

		return null;

	}

	return new URLSearchParams( window.location.search ).get( ACTIVATION_PARAM );

};

/**
 * Reads the promotion ID for the Promotion Detail widget.
 *
 * @param {string} selector CSS selector of the widget container.
 * @return {string|null} The promotion ID, or null if not present.
 */
const getPromotionId = ( selector ) => {

	const fromDom = document.querySelector( selector )?.dataset.promotionId;

	return fromDom || new URLSearchParams( window.location.search ).get( PROMOTION_DETAIL_PARAM );

};

/**
 * Runs a callback once a target element appears inside the widget container.
 * go_fw's show* methods render asynchronously with no completion signal, so
 * this watches the DOM until the expected markup (e.g. `.MuiList-root`) shows up.
 *
 * @param {HTMLElement} container         The widget container element.
 * @param {string}      targetSelector    CSS selector to wait for inside the container.
 * @param {Function}    callback          Runs with the found target element.
 * @param {boolean}     observeSubsequent Whether to observe subsequent target mounts.
 * @return {void}
 */
const onGoWidgetRendered = ( container, targetSelector, callback, observeSubsequent = false ) => {

	if ( ! container ) {

		return;

	}

	const existingTarget = container.querySelector( targetSelector );

	if ( existingTarget ) {

		callback( existingTarget );

		if ( ! observeSubsequent ) {

			return;

		}

	}

	const observer = new MutationObserver( () => {

		const target = container.querySelector( targetSelector );

		if ( ! target ) {

			return;

		}

		if ( ! observeSubsequent ) {

			observer.disconnect();

		}

		callback( target );

	} );

	observer.observe( container, { childList: true, subtree: true } );

};

/**
 * Sends a number_check event when the check button is pressed in the number
 * checker widget, with the game picked earlier.
 * go_fw exposes no events for this, so clicks are delegated from the container.
 * A game is identified by the alt text of the logo inside its button; picking
 * it only remembers the name.
 *
 * @param {string} selector CSS selector of the widget container.
 * @return {void}
 */
const trackNumberCheckClicks = ( selector ) => {

	const container = document.querySelector( selector );
	let selectedGame = '';

	container?.addEventListener( 'click', ( event ) => {

		const gameButton = event.target.closest( 'button:has(img)' );

		if ( gameButton ) {

			selectedGame = gameButton.querySelector( 'img' ).alt;

			return;

		}

		if ( event.target.closest( 'button.MuiButton-root' ) ) {

			window.MNLAnalytics.track( 'number_check', {
				...getParameters( container.closest( '.b-widgets' ) ),
				utility_name: 'number_checker',
				game_name: selectedGame,
			} );

		}

	} );

};

/**
 * Sends a ticket_check event when a ticket is submitted in the ticket entry widget.
 * go_fw exposes no events for this, so clicks are delegated from the container.
 * The widget renders a separate card with its own Submit button per ticket type;
 * the lotto card is recognised by its entry code field.
 *
 * @param {string} selector CSS selector of the widget container.
 * @return {void}
 */
const trackTicketCheckClicks = ( selector ) => {

	const container = document.querySelector( selector );

	container?.addEventListener( 'click', ( event ) => {

		const submitButton = event.target.closest( 'button[aria-label="Submit"]' );

		if ( ! submitButton ) {

			return;

		}

		const isLotto = !! submitButton.closest( '.MuiCard-root' )?.querySelector( '#drawTicketNumber' );

		window.MNLAnalytics.track( 'ticket_check', {
			...getParameters( container.closest( '.b-widgets' ) ),
			utility_name: 'ticket_entry',
			ticket_type: isLotto ? 'lotto' : 'scratch',
		} );

	} );

};

/**
 * Modifies the settings widget by adding a "Sign Out" item if it doesn't exist.
 *
 * @param {HTMLElement} selector The settings widget container element.
 *
 * @return {void}
 */
const modifySettingsWidget = ( selector ) => {

	const container = document.querySelector( selector );

	if ( ! container ) {

		return;

	}

	/**
	 * Injects the sign-out element into the correct settings UI for current viewport.
	 *
	 * @return {void}
	 */
	const syncSettingsWidget = () => {

		if ( mobileBreakPoint.matches ) {

			// Mobile.
			if ( container.dataset.goSettingsMobileObserverBound !== '1' ) {

				container.dataset.goSettingsMobileObserverBound = '1';
				onGoWidgetRendered( document, '.MuiPopover-root .MuiPaper-root .MuiList-root.MuiMenu-list', addSignOutMobileItem, true );

			}

		} else {

			// Desktop.
			onGoWidgetRendered( container, '.MuiList-root', addSignOutDesktopItem );

		}

	};

	if ( container.dataset.goSettingsBreakpointListenerBound !== '1' ) {

		container.dataset.goSettingsBreakpointListenerBound = '1';
		mobileBreakPoint.addEventListener( 'change', syncSettingsWidget );

	}

	syncSettingsWidget();

}

/**
 * Describes the widget's submit button for analytics.js, which tracks it as an account_action.
 *
 * The button is a disabled <button type="submit"> until the form is valid, so only valid submissions are
 * tracked. Attributes go on the form when there is one (handled by the submit listener), otherwise on the
 * button itself (handled by the click listener). The widget re-renders, so markup is re-marked on mutations.
 *
 * @param {HTMLElement|null} container  The widget container element.
 * @param {string}           actionType Stable account action type.
 * @return {void}
 */
const markSubmitAsAccountAction = ( container, actionType ) => {

	onGoWidgetRendered( container, 'button[type="submit"]', ( button ) => {

		const target = button.form ?? button;

		target.dataset.mnlAction = 'account_action';
		target.dataset.mnlActionType = actionType;
		target.dataset.mnlActionStatus = 'submitted';
		target.dataset.mnlSource = container.closest( '.b-widgets' ).dataset.mnlSource;

	}, true );

};

/**
 *
 * @param {string} selector
 * @return {void}
 */
const modifyRegisterWidget =( selector ) => {

	const container = document.querySelector( selector );
	onGoWidgetRendered( container, '.MuiGrid-root', ( target ) => {

		changeRegisterWidgetsTexts( target );
		enableRegisterDateOfBirthPicker( target );

	} );
	markSubmitAsAccountAction( container, 'register' );

}

/**
 *
 * @param {string} selector
 * @return {void}
 */
const modifyLoginWidget = ( selector ) => {

	const container = document.querySelector( selector );
	onGoWidgetRendered( container, '.MuiGrid-root', changeLoginWidgetsTexts );

}

/**
 *
 * @param {string} selector
 * @return {void}
 */
const modifyForgotPasswordWidget = ( selector ) => {

	const container = document.querySelector( selector );
	onGoWidgetRendered( container, '.MuiGrid-root', changeForgotPasswordWidgetsTexts );
	markSubmitAsAccountAction( container, 'password_reset' );

}

/**
 * Guards a widget render flow behind go_fw session validation.
 * Redirects silently to sign-in on invalid session or request failure.
 *
 * @param {HTMLElement} block Widget block wrapper.
 * @param {Function} onAuthorized Callback executed for valid session.
 * @return {void}
 */
const withAuthorizedSession = ( block, onAuthorized ) => {

	getGoSession().then( ( session ) => {

		const isValid = !! ( session && session.isValid );

		if ( isValid ) {

			block.querySelector( '.b-widgets__inner' )?.classList.remove( 'b-widgets-hide-content' );
			onAuthorized( session );
			return;

		}

		window.location.href = SIGN_IN_PATH;

	} ).catch( () => {

		window.location.href = SIGN_IN_PATH;

	} );

}

/**
 * Redirect to the appropriate page based on session validity.
 * @param {Function} onAuthorized Callback executed for valid session.
 * @return {void}
 */
const withNotAuthorizedSession = ( onAuthorized ) => {

	getGoSession().then( ( session ) => {

		const isValid = !! ( session && session.isValid );

		if ( isValid ) {

			window.location.href = ACCOUNT_PATH;
			return;

		}

		onAuthorized( session );

	} ).catch( () => {

		window.location.href = SIGN_IN_PATH;

	} );

}

/**
 *
 * @param {string} str
 * @return {string}
 */
const sanitizeTitle = ( str ) => {

	if ( typeof str !== 'string' ) {

		return '';

	}

	return str
		.toLowerCase()
		.replace( /[^a-z0-9\s-]/g, '' )
		.trim()
		.replace( /\s+/g, '-' )
		.replace( /-+/g, '-' );

};

/**
 * Registers the go_fw session/login listeners once for the whole page.
 *
 * @param {HTMLElement} block Widget block whose analytics source describes the login.
 * @return {void}
 */
const initGoSession = ( block ) => {

	// eslint-disable-next-line no-undef, camelcase
	if ( typeof go_fw === 'undefined' || goSessionInitialized ) {

		return;

	}

	goSessionInitialized = true;

	// Assume the player is logged out until go_fw says otherwise, so the
	// page always has a consistent state (avoids "no class at all" on load).
	document.body.classList.add( 'go-unauthenticated' );

	/**
	 * Applies a SessionObj ({ isValid, sessionToken }) to the page: toggles
	 * the `go-authenticated` / `go-unauthenticated` body classes, shows/hides
	 * the login buttons, and broadcasts a `go:sessionStatusUpdate` event.
	 *
	 * @param {Object} session SessionObj from go_fw.
	 * @return {void}
	 */
	const applySessionState = ( session ) => {

		const isValid = !! ( session && session.isValid );

		document.dispatchEvent(
			new CustomEvent( 'go:sessionStatusUpdate', { detail: session } )
		);

		document.body.classList.toggle( 'go-authenticated', isValid );
		document.body.classList.toggle( 'go-unauthenticated', ! isValid );

	};

	/**
	 * Redirects the browser to the page that should be shown after login.
	 * This is called from the go_fw onHostLogin callback.
	 *
	 * @param {Object} session SessionObj from go_fw.
	 * @return {void}
	 */
	const redirectToAfterLogin = ( session ) => {

		const isValid = !! ( session && session.isValid );

		if ( isValid ) {

			window.MNLAnalytics?.track( 'account_action', {
				...getParameters( block ),
				action_type: 'login',
				action_status: 'completed',
			} );

			window.location.href = ACCOUNT_PATH;

		} else {

			window.location.href = HOME_URL;

		}

	};

	// onSessionStatusUpdate only fires when the session state CHANGES, so on
	// page load (with an existing session) it never fires. getSessionDetails()
	// gives us the current state right away.
	getGoSession().then( applySessionState );

	// eslint-disable-next-line no-undef, camelcase
	go_fw.onSessionStatusUpdate( redirectToAfterLogin );

	// Internal links inside the go_fw widgets (e.g. the "Forgot Password?"
	// button inside showLogin) don't navigate the browser themselves — they
	// raise an onHostNavigate event instead and expect the host to react.
	// eslint-disable-next-line no-undef, camelcase
	go_fw.onHostNavigate( ( navigate ) => {

		switch ( navigate.action ) {

		// ── Page-level navigations (no login widget required) ──────────────

		case 'HISTORY':

			window.location.href = navigate.data
				? `${ HISTORY_PATH }?ht=${ navigate.data }`
				: HISTORY_PATH;
			break;

		case 'PROMOTION_DETAILS': {

			const basePath = activePromotionListType === 'second-chance'
				? SECOND_CHANCE_DETAIL_PATH
				: GIVEAWAY_DETAIL_PATH;

			const promotionId = navigate?.data?.id || '';
			const activePromotionBlock = activePromotionListType
				? document.querySelector( `.b-widgets--${ activePromotionListType }` )
				: null;
			const shouldRedirectToGameUrl = activePromotionBlock?.dataset.redirectToGameUrl === '1';
			let detailSlug;

			if ( shouldRedirectToGameUrl ) {

				detailSlug = sanitizeTitle( navigate?.data?.name || '' );

			}

			const detailPath = detailSlug ? `${ basePath }/${ detailSlug }` : basePath;
			const url = new URL( detailPath, window.location.origin );

			if ( promotionId && ! shouldRedirectToGameUrl ) {

				url.searchParams.set( PROMOTION_DETAIL_PARAM, promotionId );

			}

			window.location.href = `${ url.pathname }${ url.search }`;
			break;

		}

		case 'NUMBER_CHECKER':

			window.location.href = navigate.data?.gameId
				? `${ NUMBER_CHECKER_PATH }?id=${ navigate.data.gameId }`
				: NUMBER_CHECKER_PATH;
			break;

		case 'TICKET_ENTRY':

			window.location.href = TICKET_ENTRY_PATH;
			break;

		case 'TERMS_AND_CONDITIONS':

			window.location.href = TERMS_OF_USE_PATH;
			break;

		case 'PRIVACY_POLICY':

			window.location.href = PRIVACY_POLICY_PATH;
			break;

			// ── In-place widget swap (only when a login widget is active) ──────

		case 'FORGOT_PASSWORD':

			window.location.href = FORGOT_PASSWORD_PATH;
			break;

		case 'LOGIN':

			window.location.href = SIGN_IN_PATH;
			break;

		case 'REGISTER':

			window.location.href = REGISTER_PATH;
			break;

		default:
			// Helps identify host-side routes required by new/changed widget flows.
			// eslint-disable-next-line no-console
			console.warn( 'Unhandled go_fw navigation action:', navigate?.action, navigate );
			break;

		}

	} );

};

const blocks = document.querySelectorAll( '.b-widgets' );

/**
 * Initializes all widgets on the page after GO framework signals readiness.
 */
const initAllWidgets = () => {

	blocks.forEach( ( block ) => {

		const widgetId = block.getAttribute( 'data-widget-id' );

		initGoSession( block );

		// eslint-disable-next-line no-undef, camelcase
		if ( typeof go_fw !== 'undefined' && widgetId ) {

			const selector = `#${ widgetId }`;

			// activationKey is only present on the activation page,
			// so if it's there we know we're on that page and should show the activation widget instead of login.
			const activationKey = getActivationKey();


			// If we're on the activation page and the URL has an activation
			// key, complete the account activation instead of showing login.
			if ( activationKey ) {

				// eslint-disable-next-line no-undef, camelcase
				go_fw.showActivation( selector, activationKey );

				return;

			}

			if ( block.classList.contains( 'b-widgets--register' ) ) {

				withNotAuthorizedSession( () => {

					// eslint-disable-next-line no-undef, camelcase
					go_fw.showRegister( selector );
					modifyRegisterWidget( selector );

				} );

			} else if ( block.classList.contains( 'b-widgets--forgot-password' ) ) {

				withNotAuthorizedSession( () => {

					// eslint-disable-next-line no-undef, camelcase
					go_fw.showForgotPassword( selector );
					modifyForgotPasswordWidget( selector );

				} );

			} else if ( block.classList.contains( 'b-widgets--numbers' ) ) {

				// eslint-disable-next-line no-undef, camelcase
				go_fw.showNumberChecker( selector );
				trackNumberCheckClicks( selector );

			} else if ( block.classList.contains( 'b-widgets--history' ) ) {

				withAuthorizedSession( block, () => {

					const params = new URLSearchParams( window.location.search );
					const historyTab = params.get( 'ht' );

					// eslint-disable-next-line no-undef, camelcase
					go_fw.showHistory( selector, historyTab || undefined );

				} );

			} else if ( block.classList.contains( 'b-widgets--settings' ) ) {

				withAuthorizedSession( block, () => {

					// eslint-disable-next-line no-undef, camelcase
					go_fw.showSettings( selector );
					modifySettingsWidget( selector );

				} );

			} else if ( block.classList.contains( 'b-widgets--ticket' ) ) {

				withAuthorizedSession( block, () => {

					// eslint-disable-next-line no-undef, camelcase
					go_fw.showTicketEntry( selector );
					trackTicketCheckClicks( selector );

				} );

			} else if ( block.classList.contains( 'b-widgets--lotto' ) ) {

				// eslint-disable-next-line no-undef, camelcase
				go_fw.showLottoGamesUnclaimedPrizes( selector );

			} else if ( block.classList.contains( 'b-widgets--scratch' ) ) {

				// eslint-disable-next-line no-undef, camelcase
				go_fw.showScratchGamesUnclaimedPrizes( selector );

			} else if ( block.classList.contains( 'b-widgets--giveaways' ) ) {

				const promotionId = getPromotionId( selector );

				if ( promotionId ) {

					// eslint-disable-next-line no-undef, camelcase
					go_fw.showPromotionDetail( selector, promotionId );

				} else {

					activePromotionListType = 'giveaways';
					// eslint-disable-next-line no-undef, camelcase
					go_fw.showPromotionList( selector, 'GIVEAWAY' );

				}

			} else if ( block.classList.contains( 'b-widgets--second-chance' ) ) {

				const promotionId = getPromotionId( selector );

				if ( promotionId ) {

					// eslint-disable-next-line no-undef, camelcase
					go_fw.showPromotionDetail( selector, promotionId );

				} else {

					activePromotionListType = 'second-chance';
					// eslint-disable-next-line no-undef, camelcase
					go_fw.showPromotionList( selector, 'SECOND_CHANCE' );

				}

			} else if ( block.classList.contains( 'b-widgets--promotion-detail' ) ) {

				const promotionId = getPromotionId( selector );

				if ( promotionId ) {

					// eslint-disable-next-line no-undef, camelcase
					go_fw.showPromotionDetail( selector, promotionId );

				}

			} else {

				withNotAuthorizedSession( () => {

					// eslint-disable-next-line no-undef, camelcase
					go_fw.showLogin( selector );
					modifyLoginWidget( selector );

				} );

			}

		}

	} );

};

onGoReady( initAllWidgets );
loadGoFramework();
